import { MutationCache, QueryCache, QueryClient, type QueryFunction } from '@tanstack/react-query';
import {
  blockFor,
  isBlocked,
  msRemaining,
  resolveRetryAfterMs,
  waitForGate,
} from './rateLimitGate';
import { saveReturnPath, SIGN_IN_RETURN_PATH_KEY } from './returnPath';

interface ApiErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  retryAfterMs?: number;
  timestamp?: string;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: Record<string, unknown>;
  readonly responseText: string;

  constructor(status: number, message: string, responseText: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.responseText = responseText;
  }
}

/**
 * Subclass of ApiRequestError that tags a 429 — middleware path (rate-limit.middleware.ts:139-145)
 * or controller-throw path (profile.service.ts:79). 868mbzt38 D2 lets callers
 * branch on `instanceof RateLimitedError` without inspecting `status`. The
 * `retryAfterMs` already passed through `resolveRetryAfterMs` (header → body →
 * default, capped at 5 min).
 */
export class RateLimitedError extends ApiRequestError {
  readonly retryAfterMs: number;

  constructor(retryAfterMs: number, message = 'Too many requests.', responseText = '', details?: Record<string, unknown>) {
    super(429, message, responseText, 'RATE_LIMITED', details);
    this.name = 'RateLimitedError';
    this.retryAfterMs = retryAfterMs;
  }
}

// Company selection storage key (shared with CompanyProvider)
const COMPANY_STORAGE_KEY = 'alpro:selectedCompanyId';

// Legacy localStorage sentinel from SA-34 CA-03 (the "All organizations"
// whole-org option). CompanyProvider never writes it now, but old
// sessions can still have it on disk; queryClient must NOT echo it back
// to the server (the middleware no longer recognises it).
const LEGACY_ALL_ORGANIZATIONS_SENTINEL = '__all__';

function shouldAttachCompanyHeader(url: string): boolean {
  if (url.startsWith('/api/companies') || url.startsWith('/api/auth')) {
    return false;
  }
  // Post SA-15 the explicit `?organizationId=` query param is ignored by
  // the general resource controllers (tenant middleware is the single
  // source of truth for org scope). We keep the early-skip behaviour so
  // the legacy SA-03 dashboard selector still works: it relied on the
  // organisationId query param being passed through to the dedicated
  // `/api/pm-dashboard/sa-kpis` endpoint.
  if (/[?&]organizationId=[^&]/.test(url)) {
    return false;
  }
  return true;
}

/** Read the currently selected company ID from localStorage. */
export function getSelectedCompanyId(): string | null {
  try {
    const value = localStorage.getItem(COMPANY_STORAGE_KEY);
    // Treat the legacy whole-org sentinel as "no selection" so callers
    // can rely on a plain UUID or null.
    if (value === LEGACY_ALL_ORGANIZATIONS_SENTINEL) {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

/**
 * Clear the persisted company selection. Call on the auth boundary (login/logout)
 * so a prior session's company does not leak into the next session's X-Company-Id.
 */
export function clearSelectedCompany(): void {
  try {
    localStorage.removeItem(COMPANY_STORAGE_KEY);
  } catch {
    // localStorage unavailable — ignore
  }
}

/**
 * Returns true iff the persisted selection is the legacy whole-org sentinel.
 * Kept for callers that need to detect the legacy state explicitly (the
 * CompanyProvider uses it to clear the stale value); header emission no
 * longer branches on it.
 */
export function isAllOrganizationsSelected(): boolean {
  try {
    return localStorage.getItem(COMPANY_STORAGE_KEY) === LEGACY_ALL_ORGANIZATIONS_SENTINEL;
  } catch {
    return false;
  }
}

// CSRF Token Management
let knownCompanyIds: Set<string> | null = null;
let onCompanyAccessDenied: (() => void) | null = null;
let onCompanyRequestSuccess: (() => void) | null = null;

export function setKnownCompanyIds(ids: string[]): void {
  knownCompanyIds = new Set(ids);
}

export function setCompanyAccessDeniedHandler(fn: (() => void) | null): void {
  onCompanyAccessDenied = fn;
}

export function setCompanyRequestSuccessHandler(fn: (() => void) | null): void {
  onCompanyRequestSuccess = fn;
}

let csrfToken: string | null = null;
let csrfTokenPromise: Promise<string> | null = null;

async function fetchCsrfToken(): Promise<string> {
  const res = await fetch('/csrf-token', {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error('Failed to fetch CSRF token');
  }
  const data = await res.json();
  return data.csrfToken;
}

export async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }

  // Prevent multiple simultaneous requests for CSRF token
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetchCsrfToken().then((token) => {
      csrfToken = token;
      csrfTokenPromise = null;
      return token;
    }).catch((err) => {
      csrfTokenPromise = null;
      throw err;
    });
  }

  return csrfTokenPromise;
}

// Clear CSRF token (call on logout or when token is invalid)
export function clearCsrfToken(): void {
  csrfToken = null;
  csrfTokenPromise = null;
}

// Check if method requires CSRF protection
function isMutationMethod(method: string): boolean {
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeErrorBody(value: unknown): ApiErrorBody | null {
  if (!isRecord(value)) { return null; }

  const rawError = value.error;
  const source = isRecord(rawError) ? rawError : value;
  const code = typeof source.code === 'string' ? source.code : undefined;
  const message = typeof source.message === 'string' ? source.message : undefined;
  const details = isRecord(source.details) ? source.details : undefined;
  const timestamp = typeof source.timestamp === 'string' ? source.timestamp : undefined;
  // 868mbzt38 D2 — pull retryAfterMs from the two shapes the unified
  // limiter emits: middleware path puts it next to `code`/`message`
  // (`error: { retryAfterMs }`), and the controller-throw path (the new
  // bug fix here) keeps it inside `error.details` because `createAppError`
  // wraps the value there. `resolveRetryAfterMs` reads `bodyMs` and
  // applies its own precedence.
  const topLevelRetryMs = typeof source.retryAfterMs === 'number' ? source.retryAfterMs : undefined;
  const detailsRetryMs =
    typeof details?.retryAfterMs === 'number' ? details.retryAfterMs : undefined;
  const retryAfterMs = topLevelRetryMs ?? detailsRetryMs;

  if (!code && !message && !details && !retryAfterMs && !timestamp) { return null; }
  return { code, message, details, retryAfterMs, timestamp };
}

function parseErrorText(text: string): ApiErrorBody | null {
  if (!text) { return null; }
  try {
    return normalizeErrorBody(JSON.parse(text));
  } catch {
    return null;
  }
}

export async function createApiRequestError(res: Response): Promise<ApiRequestError> {
  const responseText = await res.text().catch(() => '');
  const body = parseErrorText(responseText);
  const message = body?.message || responseText || res.statusText;
  return new ApiRequestError(res.status, message, responseText, body?.code, body?.details);
}

/**
 * 868mbzt38 D2 — pull `retryAfterMs` out of a 429 response and decide whether
 * to (a) throw `RateLimitedError`, (b) activate the gate, or both. Encapsulated
 * so `executeRequest` and `getQueryFn` share one path.
 */
function buildRateLimitedError(
  res: Response,
  baseMessage: string,
  responseText: string,
  body: ApiErrorBody | null,
): RateLimitedError {
  const headerValue = res.headers.get('retry-after');
  // `normalizeErrorBody` already flattens `error.retryAfterMs` (middleware
  // path) and `error.details.retryAfterMs` (controller-throw path) into
  // `body.retryAfterMs` in the order the r2.1 addendum fixed: top-level
  // first, then details. Use that single normalized value here.
  const bodyMs = typeof body?.retryAfterMs === 'number' ? body.retryAfterMs : null;
  const retryAfterMs = resolveRetryAfterMs(headerValue, bodyMs);
  return new RateLimitedError(retryAfterMs, baseMessage, responseText, body?.details);
}

// [SA-33 / 868kbjw74 EX-03] A plain 403 means the permissions the client holds
// may be stale (e.g. User Management revoked from another session). Refetching
// /api/auth/user at the next request makes the sidebar drop the section and the
// route guard close the screen; the 60 s poll in useAuth stays as a fallback.
// Throttled so a screen that fires several denied requests refetches once.
const PERMISSION_REFRESH_MIN_GAP_MS = 5_000;
let lastPermissionRefreshAt = 0;
let deferredPermissionRefresh: ReturnType<typeof setTimeout> | null = null;

function refreshPermissionsAfterForbidden(requestUrl: string): void {
  let pathname = requestUrl;
  try {
    pathname = new URL(requestUrl, window.location.origin).pathname;
  } catch {
    // keep the raw value; the includes() check below is best effort
  }
  if (pathname.startsWith('/api/auth/')) {
    return;
  }
  const now = Date.now();
  const sinceLast = now - lastPermissionRefreshAt;
  if (sinceLast < PERMISSION_REFRESH_MIN_GAP_MS) {
    // A 403 inside the window may carry a permission change that the refresh
    // already in flight did not see: refresh once more when the window ends.
    if (deferredPermissionRefresh === null) {
      deferredPermissionRefresh = setTimeout(() => {
        deferredPermissionRefresh = null;
        lastPermissionRefreshAt = Date.now();
        void queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      }, PERMISSION_REFRESH_MIN_GAP_MS - sinceLast);
    }
    return;
  }
  lastPermissionRefreshAt = now;
  void queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
}

// 401 errors of `executeRequest`/`getQueryFn`: the session check already ran
// for them, so the cache-level `onError` does not run it a second time.
const sessionCheckedErrors = new WeakSet<Error>();

/**
 * Throw an error appropriate to the response status. Owns 403's CSRF and
 * company-access side-effects. 429s are pre-handled by `executeRequest`
 * (which knows the method) and by `getQueryFn` (always GET); they don't
 * reach this helper. Returns normally when `res.ok` is true.
 */
async function throwIfResNotOk(res: Response): Promise<void> {
  if (!res.ok) {
    const error = await createApiRequestError(res);
    // Both callers ran `handleUnauthorized` on a 401 before getting here.
    if (res.status === 401) {
      sessionCheckedErrors.add(error);
    }

    // If CSRF token is invalid, clear it so next request fetches a new one
    if (res.status === 403) {
      if (
        error.code === 'COMPANY_ACCESS_DENIED' ||
        error.code === 'CROSS_ORG_COMPANY_ACCESS'
      ) {
        onCompanyAccessDenied?.();
      } else if (error.responseText.includes('CSRF') || error.responseText.includes('csrf')) {
        clearCsrfToken();
      } else {
        refreshPermissionsAfterForbidden(res.url);
      }
    }

    throw error;
  }
}

/**
 * Build request headers (CSRF, X-Company-Id) and execute a request with
 * the shared tenant-side-error hook. Returns the raw Response so the
 * caller decides how to unwrap the envelope.
 */
async function executeRequest(
  url: string,
  options: {
    method?: string;
    body?: BodyInit | null;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  } = {},
): Promise<Response> {
  const { method = 'GET', body, headers = {}, signal } = options;

// 868mbzt38 D2 — gate before dispatching. GETs queue until the gate
    // releases; mutations are short-circuited with RateLimitedError so a
    // blocked GET can't accidentally trigger a random mutation.
    if (isMutationMethod(method)) {
      if (isBlocked()) {
        throw new RateLimitedError(msRemaining());
      }
    } else {
      await waitForGate(signal);
    }

  const requestHeaders: Record<string, string> = {
    ...headers,
    ...(typeof body === 'string' && !headers['Content-Type'] ? { 'Content-Type': 'application/json' } : {}),
  };

  // CR1-FE-02: Inject X-Company-Id for company-scoped requests
  const companyId = getSelectedCompanyId();
  const localStorageHasCompany = companyId !== null;
  const knownSetHasCompany = companyId !== null && (knownCompanyIds?.has(companyId) ?? false);
  if (companyId && shouldAttachCompanyHeader(url) && (knownSetHasCompany || localStorageHasCompany)) {
    requestHeaders['X-Company-Id'] = companyId;
  }

  if (isMutationMethod(method)) {
    try {
      const token = await ensureCsrfToken();
      requestHeaders['X-CSRF-Token'] = token;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to get CSRF token:', err);
    }
  }

  const res = await fetch(url, {
    method,
    headers: requestHeaders,
    body,
    credentials: 'include',
    signal,
  });

  if (res.status === 429) {
    const text = await res.text().catch(() => '');
    const body = parseErrorText(text);
    const error = buildRateLimitedError(
      res,
      body?.message || text || res.statusText,
      text,
      body,
    );
    // D2 — only GETs activate the gate. Mutations don't freeze the rest
    // of the app; they fail loudly at the call site.
    if (method.toUpperCase() === 'GET') {
      blockFor(error.retryAfterMs);
    }
    throw error;
  }

  if (res.status === 401) {
    await handleUnauthorized(url);
  }

  await throwIfResNotOk(res);
  if (shouldAttachCompanyHeader(url)) {
    onCompanyRequestSuccess?.();
  }
  return res;
}

async function unwrapResponseBody<T>(res: Response, envelope: 'data' | 'both'): Promise<T | { data: T; meta?: ApiMetadata }> {
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return (envelope === 'both' ? { data: null as T } : null) as never;
  }

  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const json = await res.json();
    return envelope === 'both' ? unwrapEnvelopeWithMeta<T>(json) : (unwrapEnvelope(json) as T);
  }

  const text = await res.text();
  return (envelope === 'both' ? { data: (text || null) as T } : ((text || null) as T)) as never;
}

export async function apiRequest<T = unknown>(
  url: string,
  options?: {
    method?: string;
    body?: BodyInit | null;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  }
): Promise<T> {
  const res = await executeRequest(url, options);
  return unwrapResponseBody<T>(res, 'data') as Promise<T>;
}

export async function apiRequestWithMeta<T = unknown>(
  url: string,
  options?: {
    method?: string;
    body?: BodyInit | null;
    headers?: Record<string, string>;
    signal?: AbortSignal;
  }
): Promise<{ data: T; meta?: ApiMetadata }> {
  const res = await executeRequest(url, options);
  return unwrapResponseBody<T>(res, 'both') as Promise<{ data: T; meta?: ApiMetadata }>;
}

/**
 * Unwrap standardized API envelope { success, data } → data.
 * Pass through if the response is not in envelope format.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unwrapEnvelope(json: any): any {
  if (
    json !== null &&
    typeof json === 'object' &&
    'success' in json &&
    'data' in json
  ) {
    return json.data;
  }
  return json;
}

/**
 * Server response metadata envelope. The shape mirrors the server's
 * `ApiMetadata`; keeping it loose at the boundary so additional
 * endpoint-specific fields can ride on the same meta without a
 * frontend-wide type change.
 */
export interface ApiMetadata {
  warnings?: TaxIdFormatWarning[];
  [key: string]: unknown;
}

/**
 * SA-31 EX-02 — non-blocking warning returned alongside the company on
 * mutations where the Tax ID does not match the country regex. The
 * controller copies it into `response.meta.warnings` so the form can
 * surface it as an inline alert under the Tax ID field without losing
 * the successful write.
 */
export interface TaxIdFormatWarning {
  code: 'COMPANY_TAX_ID_FORMAT_SUSPECT';
  message: string;
  details: {
    country: string;
    taxIdLength: number;
    expectedPattern?: string;
    companyId?: string;
  };
}

/**
 * Unwrap a standardized envelope `{ success, data, meta }` and return
 * both `data` and the optional `meta` block. Use this instead of
 * `apiRequest` when the caller needs to read meta (e.g. SA-31 EX-02
 * warnings). Non-envelope responses return `{ data: <raw> }` with no
 * meta, matching the legacy pass-through behaviour of `unwrapEnvelope`.
 */
export function unwrapEnvelopeWithMeta<T>(json: unknown): { data: T; meta?: ApiMetadata } {
  if (
    json !== null &&
    typeof json === 'object' &&
    'success' in json &&
    'data' in json
  ) {
    const obj = json as { data: T; meta?: ApiMetadata };
    return { data: obj.data, meta: obj.meta };
  }
  return { data: json as T };
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey, signal }) => {
    const url = queryKey.join("/") as string;

    // 868mbzt38 D2 — wait for the gate before fetching. `signal` is the
    // AbortSignal from TanStack's QueryFunctionContext; an aborted query
    // cancels the wait too.
    await waitForGate(signal);

    // CR1-FE-02: Build headers with X-Company-Id for company-scoped queries.
    // Post SA-15: the legacy whole-org sentinel is treated as "no selection"
    // by `getSelectedCompanyId`, so the header is simply omitted.
    const headers: Record<string, string> = {};
    const companyId = getSelectedCompanyId();
    if (companyId && shouldAttachCompanyHeader(url) && knownCompanyIds?.has(companyId)) {
      headers['X-Company-Id'] = companyId;
    }

    const res = await fetch(url, {
      credentials: "include",
      headers,
      signal,
    });

    if (res.status === 429) {
      const text = await res.text().catch(() => '');
      const body = parseErrorText(text);
      const error = buildRateLimitedError(
        res,
        body?.message || text || res.statusText,
        text,
        body,
      );
      // getQueryFn is only mounted under `useQuery`, so the method is
      // GET for every caller — block unconditionally here.
      blockFor(error.retryAfterMs);
      throw error;
    }

    if (res.status === 401) {
      await handleUnauthorized(url);
      if (unauthorizedBehavior === "returnNull") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return null as any;
      }
    }

    await throwIfResNotOk(res);
    if (shouldAttachCompanyHeader(url)) {
      onCompanyRequestSuccess?.();
    }
    const json = await res.json();
    return unwrapEnvelope(json);
  };

export const queryClient = new QueryClient({
  // 868mjkvg2 CA-07 — the legacy raw `fetch('/api/...')` hooks bypass
  // `executeRequest`/`getQueryFn` and throw a plain `Error('401: ...')`;
  // the caches route those through the same session check.
  queryCache: new QueryCache({ onError: handleOpaqueUnauthorized }),
  mutationCache: new MutationCache({ onError: handleOpaqueUnauthorized }),
  defaultOptions: {
    queries: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryFn: getQueryFn({ on401: "throw" }) as any,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// ─── 868mjkvg2 CA-07: a closed session sends the user to the login ──────────
//
// Resetting the password or confirming an email change deletes every session
// of the account on the server. The old browser still holds the signed-in user
// in the cache (`staleTime: Infinity`), so until the next `/api/auth/user` poll
// every new section answered 401 and showed «Failed to load data» or an empty
// list. Now the first 401 of a signed-in page ends the session on the client:
// the cached user is dropped and the browser goes to the login screen.

const AUTH_USER_URL = '/api/auth/user';

// Pages that work without a session (and the login itself): a 401 there is
// theirs to show, never a reason to navigate — no redirect loop on /login.
const SIGNED_OUT_PATHS = ['/login', '/forgot-password', '/reset-password', '/verify-email-change', '/email-preferences'];

/** The navigation, apart so the unit tests can observe it without leaving jsdom. */
export const sessionRedirect = {
  toLogin(): void {
    // A full load, like the logout: no cached data of the closed session
    // survives for whoever signs in next in this tab.
    window.location.replace('/login');
  },
};

// A check that hangs must not keep the screen loading: past this, the session
// counts as alive and the caller gets its 401 as before.
const SESSION_CHECK_TIMEOUT_MS = 5_000;

let sessionCheck: Promise<boolean> | null = null;

/**
 * Some endpoints answer 401 for reasons other than a missing session (a wrong
 * password, a cross-org report). The session is gone only when
 * `/api/auth/user` answers 401 as well; concurrent 401s share one check.
 */
function isSessionGone(): Promise<boolean> {
  if (!sessionCheck) {
    // Raw fetch on purpose: only the status is read, there is no envelope to unwrap.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SESSION_CHECK_TIMEOUT_MS);
    sessionCheck = fetch(AUTH_USER_URL, { credentials: 'include', signal: controller.signal })
      .then((res) => res.status === 401)
      .catch(() => false)
      .finally(() => {
        clearTimeout(timer);
        sessionCheck = null;
      });
  }
  return sessionCheck;
}

function cachedUser(): unknown {
  return queryClient.getQueryData([AUTH_USER_URL]);
}

function isOnSignedOutPage(): boolean {
  const path = window.location.pathname;
  return SIGNED_OUT_PATHS.some((page) => path === page || path.startsWith(`${page}/`));
}

/**
 * Called on every 401 before the error reaches the caller, so the page never
 * renders it. Without a cached user the app is already signed out and nothing
 * happens; a 401 of `/api/auth/user` itself needs no second check.
 */
export async function handleUnauthorized(url: string): Promise<void> {
  const user = cachedUser();
  if (!user || isOnSignedOutPage()) {
    return;
  }
  if (url !== AUTH_USER_URL && !(await isSessionGone())) {
    return;
  }
  // The cache changed while the check ran: another 401 of the same burst
  // already ended it, or a new sign-in replaced the user; or the user moved
  // to a page that works signed out. Either way, nothing to end here.
  if (cachedUser() !== user || isOnSignedOutPage()) {
    return;
  }
  // A vote's email link (VOTE-02 CA-02) is followed again after signing in,
  // with its query (`/votes/new?propertyId=...`).
  saveReturnPath(`${window.location.pathname}${window.location.search}`, SIGN_IN_RETURN_PATH_KEY);
  clearCsrfToken();
  queryClient.setQueryData([AUTH_USER_URL], null);
  sessionRedirect.toLogin();
}

/**
 * 868mqancr N75 — hooks with their own fetch throw either a plain
 * `Error('401: ...')` or an `ApiRequestError` built with
 * `createApiRequestError`; both carry a 401 the session check must see.
 */
function isUncheckedUnauthorized(error: unknown): boolean {
  if (error instanceof ApiRequestError) {
    return error.status === 401 && !sessionCheckedErrors.has(error);
  }
  return error instanceof Error && /^401:/.test(error.message);
}

function handleOpaqueUnauthorized(error: unknown): void {
  if (isUncheckedUnauthorized(error)) {
    void handleUnauthorized('');
  }
}
